import {
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  ArrowRightIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ChartBarIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import React from 'react';
import { productTypes } from '@dashboard/enums/product-types';
import usePage from '@dashboard/hooks/usePage';
import getConversionRate from '@dashboard/services/getConversionRate';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import Link from '@dashboard/components/Link';
import * as KPICard from '../components/KPICard';
import RecentPayments from '../components/RecentPayments';
import RecentSubscriptions from '../components/RecentSubscriptions';
import PageOverviewHeader from '../components/PageOverviewHeader';
import { PRODUCT_TYPES } from '@/dashboard/services/getProductType';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';
import getRecoveredTooltipText from '@dashboard/services/abandonment/getRecoveredTooltipText';
import formatRecoveredStats from '@dashboard/services/abandonment/formatRecoveredStats';

export default function CheckoutOverview() {
  const { page } = usePage();
  const { product } = page;
  const { seller } = useAuth();

  const conversionRate = getConversionRate(product.chargeCount, page.visitCount);

  const recentTitle =
    page.productType === PRODUCT_TYPES.SUBSCRIPTION ? 'Recent subscriptions' : 'Recent payments';

  return (
    <div>
      <PageOverviewHeader page={page}>
        {product.type === productTypes.CHARGE && (
          <>
            <KPICard.Container>
              <KPICard.Icon icon={ChartBarIcon} />
              <KPICard.Header>
                <KPICard.Title>Revenue</KPICard.Title>
                <KPICard.InfoTooltip>
                  Gross revenue from payments before refunds
                </KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>
                {getPriceLabel({
                  amount: product.chargeTotal,
                  currency: product.currency,
                })}
              </KPICard.Value>
            </KPICard.Container>

            <KPICard.Container>
              <KPICard.Icon icon={BanknotesIcon} />
              <KPICard.Header>
                <KPICard.Title>Payments</KPICard.Title>
                <KPICard.InfoTooltip>Total number of payments</KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>{product.chargeCount || 0}</KPICard.Value>
            </KPICard.Container>
          </>
        )}

        {product.type === productTypes.SUBSCRIPTION && (
          <>
            <KPICard.Container>
              <KPICard.Icon icon={ArrowPathRoundedSquareIcon} />
              <KPICard.Header>
                <KPICard.Title>Subscriptions</KPICard.Title>
                <KPICard.InfoTooltip>Total number of subscriptions</KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>{product.subscribers || 0}</KPICard.Value>
            </KPICard.Container>
          </>
        )}

        <KPICard.Container>
          <KPICard.Icon icon={EyeIcon} />
          <KPICard.Header>
            <KPICard.Title>Page views</KPICard.Title>
            <KPICard.InfoTooltip>Total number of checkout views</KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>{page.visitCount}</KPICard.Value>
        </KPICard.Container>

        <KPICard.Container>
          <KPICard.Icon icon={ArrowTrendingUpIcon} />
          <KPICard.Header>
            <KPICard.Title>Conversion rate</KPICard.Title>
            <KPICard.InfoTooltip>Conversion Rate (conversions ÷ views × 100)</KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>{conversionRate}%</KPICard.Value>
        </KPICard.Container>

        <KPICard.Container>
          <KPICard.Icon icon={ArrowPathIcon} />
          <KPICard.Header>
            <KPICard.Title>Recovered</KPICard.Title>
            <KPICard.InfoTooltip>
              {getRecoveredTooltipText(seller.checkoutAbandonment?.sendEmails)}
            </KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>
            {formatRecoveredStats({
              totalRecovered: page.abandonedCartStats?.totalRecovered,
              totalAbandoned: page.abandonedCartStats?.totalAbandoned,
              totalRecoveredRevenue: page.abandonedCartStats?.totalRecoveredRevenue,
              currency: product.currency,
            })}
          </KPICard.Value>
        </KPICard.Container>
      </PageOverviewHeader>

      <div className="mt-8">
        <div className="flex justify-between items-center ml-4">
          <h2 className="text-xl font-medium">{recentTitle}</h2>

          <div>
            <Link
              href={`/pages/${page._id}/${
                page.product.type === 'subscription' ? 'subscriptions' : 'payments'
              }`}
              className="flex items-center gap-x-1 whitespace-nowrap text-sm"
            >
              View all <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {page.product.type === 'subscription' && <RecentSubscriptions />}
        {page.product.type === 'charge' && <RecentPayments />}
      </div>
    </div>
  );
}
